import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersInsuranceFormData,
  ParametersInsuranceSchema,
} from "@/types/forms/bo/clientsParameters.form";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";

type UpdateInsuranceModalProps = {
  closeModal: () => void;
  insurance: number;
  title: string;
};

export const UpdateInsuranceModal = ({
  closeModal,
  insurance,
  title,
}: UpdateInsuranceModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const insuranceForm = useForm<ParametersInsuranceFormData>({
    resolver: zodResolver(ParametersInsuranceSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      insurancePercentage: insurance,
      optionInsuranceActive: false,
    },
  });

  const onSubmit: SubmitHandler<ParametersInsuranceFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: {
        insurancePercentage: data.insurancePercentage,
        optionInsuranceActive: true,
      },
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  const handleReset = async () => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: {
        insurancePercentage: 0,
        optionInsuranceActive: false,
      },
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="insuranceForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("insuranceModal.submit")}
        </Button>
      }
    >
      <form
        id="insuranceForm"
        onSubmit={insuranceForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledUnifiedInput
          control={insuranceForm.control}
          name="insurancePercentage"
          label={t("insuranceModal.insuranceLabel")}
          defaultValue={insurance}
          inputType="float"
        />
        <div className="flex items-center gap-2">
          <hr className="w-full" />
          <p className="text-sm text-gray-500">{t("insuranceModal.or")}</p>
          <hr className="w-full" />
        </div>
        <Button type="button" variant="ghost" onClick={handleReset} disabled={isPending}>
          {isPending ? <StyledSpinner /> : t("insuranceModal.reset")}
        </Button>
      </form>
    </Modal>
  );
};
